import { memo, useCallback, useState, useEffect } from 'react';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@eduzz/houston-ui/Button';
import { makeStyles } from '@material-ui/core/styles';

export interface IAlertShowParams {
  message: React.ReactNode;
  title?: string;
  confirmation?: boolean;
  preConfirm?: () => Promise<any>;
}

interface IAlertShowParamsState extends IAlertShowParams {
  onConfirm: () => void;
  onCancel: () => void;
}

type AlertComponent = ReturnType<typeof memo> & {
  show?(params: string): Promise<boolean>;
  show?(params: IAlertShowParams): Promise<boolean>;

  confirm?(params: string): Promise<boolean>;
  confirm?(params: IAlertShowParams): Promise<boolean>;
};

type PromiseResolveFunction = (value: boolean | PromiseLike<boolean>) => void;

let lastPromise = Promise.resolve(false);
let componentCallback: (params: IAlertShowParams) => Promise<boolean>;

const useStyle = makeStyles((theme) => {
  return {
    root: { zIndex: 1600 },
    content: { minWidth: '250px' },
    confirmButton: {
      background: theme.palette.primary.main,
      '&:hover': {
        background: theme.palette.primary.main,
      },
    },
    cancelButton: {
      color: theme.palette.error.main,
      borderColor: theme.palette.error.main,
    },
  };
});

const Alert: AlertComponent = memo(() => {
  const classes = useStyle();
  const [opened, setOpened] = useState<boolean>(false);
  const [params, setParams] = useState<IAlertShowParamsState>();
  const [loading, setLoading] = useState<boolean>(false);

  const onConfirm = useCallback((params: IAlertShowParams, resolve: PromiseResolveFunction) => {
    if (params.preConfirm) {
      setLoading(true);
      return params
        .preConfirm()
        .then(() => resolve(true))
        .finally(() => setLoading(false));
    }
    return resolve(true);
  }, []);

  const onReceiveParams = useCallback(
    (params: IAlertShowParams): Promise<boolean> => {
      const result = new Promise<boolean>((resolve) => {
        setOpened(true);
        setParams({
          confirmation: false,
          title: null,
          ...params,
          onConfirm: () => onConfirm(params, resolve),
          onCancel: () => resolve(false),
        });
      });

      result.then(() => setOpened(false));
      return result;
    },
    [onConfirm]
  );

  useEffect(() => {
    componentCallback = onReceiveParams;
    return () => (componentCallback = null);
  }, [onReceiveParams]);

  return (
    <Dialog open={opened} keepMounted onClose={params?.onCancel} className={classes.root}>
      <DialogTitle id='dialog-title'>{params?.title ?? (params?.confirmation ? 'Confirmação' : 'Atenção')}</DialogTitle>
      <DialogContent>
        <DialogContentText className={classes.content} id='dialog-content-text'>
          {params?.message}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {params?.confirmation && (
          <Button
            onClick={params?.onCancel}
            variant={'outlined'}
            className={classes.cancelButton}
            id='dialog-btn-cancel'
          >
            Cancelar
          </Button>
        )}
        <Button
          loading={loading}
          onClick={params?.onConfirm}
          variant={'contained'}
          className={classes.confirmButton}
          id='dialog-btn-confirm'
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
});

function callComponent(params: IAlertShowParams): Promise<boolean> {
  if (!componentCallback) throw new Error('Please, initialize an Alert before');

  //prevent an alert to overhide another
  return (lastPromise = lastPromise.then(async () => {
    await new Promise<void>((resolve) => setTimeout(() => resolve(), 300));
    return componentCallback(params);
  }));
}

Alert.show = (params: string | IAlertShowParams) => {
  return callComponent(typeof params === 'string' ? { message: params } : params);
};

Alert.confirm = (params: string | IAlertShowParams) => {
  return callComponent({ ...(typeof params === 'string' ? { message: params } : params), confirmation: true });
};

export default Alert;
